import { useEffect, useState } from "react";
import type { ReportSection, SectionThreeScreenshotEvidence } from "./domain";
import { getLocalFile } from "./services/local-file-store";

export function sectionThreeClientWebsite(section: ReportSection, pdfOnly = false) {
  const website = section.sectionThreeWorkflow?.website;
  return website?.url && website.clientVisible && (!pdfOnly || website.includeInPdf) ? website : undefined;
}

export function sectionThreeClientImages(section: ReportSection, pdfOnly = false) {
  const seen = new Set<string>();
  return (section.sectionThreeWorkflow?.screenshots ?? [])
    .filter(item => !item.archived && !item.internalOnly && item.clientVisible && item.fileKey && (!pdfOnly || item.includeInPdf))
    .filter(item => { const key = item.fileKey!; if (seen.has(key)) return false; seen.add(key); return true; });
}

export function sectionThreeClientSections(section: ReportSection): Array<[string, string]> {
  const draft = section.sectionThreeWorkflow?.clientDraft;
  if (!draft) return [];
  if(draft.websiteAssessment!==undefined){
    return[
      ["Website assessment",draft.websiteAssessment],
      ["Technical findings reported by Ayzeo",draft.ayzeoTechnicalFindings??""],
      ["Recommended website actions",draft.recommendedWebsiteActions??""],
      ["Evidence limitations",draft.evidenceLimitations],
    ].filter((item):item is [string,string]=>Boolean(item[1]?.trim()));
  }
  const positioning = draft.currentPositioning || draft.serviceCustomerLocation;
  const trustAndConversion = draft.trustAndConversion || [draft.trustEvidence, draft.contactJourney].filter(Boolean).join(" ");
  return [
    ["Website overview", draft.websiteOverview],
    ["What the website communicates well", draft.communicatesWell],
    ["What is unclear or missing", draft.unclearOrInconsistent],
    ["Technical findings", draft.technicalFindings],
    ["Current positioning", positioning],
    ["Trust and conversion observations", trustAndConversion],
    ["Recommended website changes", draft.recommendedChanges],
  ].filter((item): item is [string, string] => Boolean(item[1]));
}

export function sectionThreePdfText(section: ReportSection) {
  const draft = section.sectionThreeWorkflow?.clientDraft;
  if (!draft) return "";
  const website = sectionThreeClientWebsite(section, true);
  return [
    ...sectionThreeClientSections(section).flatMap(([title, text]) => [title, text]),
    ...(website ? ["Visit the website reviewed", website.url] : []),
    ...(draft.websiteAssessment!==undefined?[]:["Evidence limitations", draft.evidenceLimitations]),
    "What we will examine next", draft.nextStep,
  ].filter(Boolean).join("\n\n");
}

function LocalImage({ item, onOpen }: { item: SectionThreeScreenshotEvidence; onOpen: (url: string) => void }) {
  const [url, setUrl] = useState("");
  useEffect(() => { let active = true, current = ""; if (item.fileKey) getLocalFile(item.fileKey).then(file => { if (file && active) { current = URL.createObjectURL(file); setUrl(current); } }); return () => { active = false; if (current) URL.revokeObjectURL(current); }; }, [item.fileKey]);
  return url ? <button className="image-enlarge-button" onClick={() => onOpen(url)} aria-label={`Enlarge ${item.alternativeText || item.sourceTitle}`}><img src={url} alt={item.alternativeText || item.sourceTitle} /></button> : <div className="image-placeholder">Stored image preview</div>;
}

export function SectionThreeClientBody({ section }: { section: ReportSection }) {
  const draft = section.sectionThreeWorkflow?.clientDraft, website = sectionThreeClientWebsite(section), images = sectionThreeClientImages(section), [lightbox, setLightbox] = useState<string>();
  useEffect(() => { if (!lightbox) return; const close = (event: KeyboardEvent) => event.key === "Escape" && setLightbox(undefined); window.addEventListener("keydown", close); return () => window.removeEventListener("keydown", close); }, [lightbox]);
  if (!draft) return <p>No client-facing content has been added to this section yet.</p>;
  return <div className="section-three-client-output">
    {sectionThreeClientSections(section).map(([title, text]) => <section key={title}><h3>{title}</h3><p>{text}</p></section>)}
    {website && <section><h3>Website reviewed</h3><a href={website.url} target="_blank" rel="noopener noreferrer">Visit the website reviewed</a></section>}
    {draft.evidenceLimitations && draft.websiteAssessment===undefined && <section className="client-evidence-limitation"><h3>Evidence limitations</h3><p>{draft.evidenceLimitations}</p></section>}
    {images.length > 0 && <section><h3>Supporting screenshots</h3><div className={`client-screenshot-gallery ${images.length === 1 ? "one" : images.length === 2 ? "two" : "many"}`}>{images.map(item => <figure key={item.id} className={`client-evidence-image ${(item.imageLayout ?? "Automatic").toLowerCase().replace(" width", "").replace(" ", "-")}`}><LocalImage item={item} onOpen={setLightbox} />{item.selectedExcerpt && <figcaption>{item.selectedExcerpt}</figcaption>}</figure>)}</div></section>}
    <section><h3>What we will examine next</h3><p>{draft.nextStep}</p></section>
    {lightbox && <div className="image-lightbox" role="dialog" aria-modal="true" aria-label="Enlarged website screenshot" onClick={() => setLightbox(undefined)}><div onClick={event => event.stopPropagation()}><button className="image-lightbox-close" autoFocus onClick={() => setLightbox(undefined)}>Close enlarged image</button><img src={lightbox} alt="Enlarged website evidence" /></div></div>}
  </div>;
}
